<template>
    <div>
        <span v-if="currencySymbolInfo.position === SymbolPosition.Start" class="currencySymbol">{{ currencySymbolInfo.symbol }}</span>
        <span v-if="currencySymbolInfo.hasSpace">&nbsp;</span>
        <div
            ref="elRef"
            class="notranslate"
        />
        <span v-if="currencySymbolInfo.position === SymbolPosition.End">
            <span v-if="currencySymbolInfo.hasSpace">&nbsp;</span>
            <span class="currencySymbol">{{ currencySymbolInfo.symbol }}</span>
        </span>
    </div>
</template>

<script lang="ts">
    import { SymbolPosition } from '@sports-utils/money';
    import Decimal from 'decimal.js-light';
    import Odometer from 'odometer';
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, onMounted, ref, toRefs, watch } from 'vue';
    import { useAccountStore } from '@/stores/accountStore';
    import 'odometer/themes/odometer-theme-minimal.css';

    export default defineComponent({
        name: 'Odometer',
        props: {
            value: {
                type: Number,
                required: true,
            },
            thousandSeparator: {
                type: String,
                default: ',',
            },
            decimalSeparator: {
                type: String,
                default: '.',
            },
            duration: {
                type: Number,
                default: 1000,
            },
        },
        setup(props) {
            window.odometerOptions = { auto: false };

            const { value, thousandSeparator, decimalSeparator, duration } = toRefs(props);

            const elRef = ref<HTMLElement | null>(null);

            // to handle case where odometer will trim trailing 0 in X.X0,
            // we add additional 0.001 to value and render with 3 decimal places,
            // then hide last decimal place through css
            const valueWithExtraThirdDecimalPlace = computed(() => new Decimal(value.value).add(0.001).toNumber());

            const { currencySymbolInfo } = storeToRefs(useAccountStore());

            onMounted(() => {
                const el = elRef.value;
                if (!el) return;

                const instance = new Odometer({
                    el,
                    value: valueWithExtraThirdDecimalPlace.value,
                    format: `(${thousandSeparator.value}ddd)${decimalSeparator.value}ddd`, // render with 3 decimal places, then hide last decimal place through css
                    duration: duration.value,
                });

                watch(valueWithExtraThirdDecimalPlace, () => {
                    instance.update(valueWithExtraThirdDecimalPlace.value);
                });
            });

            return {
                elRef,
                currencySymbolInfo,
                SymbolPosition,
            };
        },
    });
</script>

<style lang="scss" scoped>
/* stylelint-disable-next-line selector-pseudo-element-no-unknown */
::v-deep .odometer-radix-mark ~ .odometer-digit:last-child {
    display: none;
}
</style>
